import { Component, OnInit, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ComplaintsService } from '../../../services/api/complaints.service';
import { HttpParams } from '@angular/common/http';
import { RouteService } from '../../../services/route.service';
import { MainLayoutService } from '../../../services/main-layout.service';
import { SelectFieldComponent } from '../../../components/form/select-field/select-field.component';
import { InputFieldComponent } from '../../../components/form/input-field/input-field.component';
import { SelectOption } from '../../../components/form/select-field/select-field.config';
import { OptionService } from '../../../services/api/option.service';

export interface ComplaintStatus {
  key: string;
  label: string;
  icon: string;
  count?: number;
}

@Component({
  selector: 'app-complaints-index-page',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SelectFieldComponent, InputFieldComponent],
  templateUrl: './complaints-index-page.component.html',
  styleUrl: './complaints-index-page.component.scss'
})
export class ComplaintsIndexPageComponent implements OnInit {
  
  complaints: any[] = [];
  searchTerm: string = '';
  canCreateComplaint: boolean = false;
  
  statusFilters: ComplaintStatus[] = [
    { key: 'OPEN', label: 'Open', icon: 'fa-gavel', count: 0 },
    { key: 'WAITING_FOR_CUSTOMER', label: 'Waiting for customer', icon: 'fa-hourglass-start', count: 0 },
    { key: 'WAITING_FOR_OPERATOR', label: 'Waiting for operator', icon: 'fa-hourglass-end', count: 0 },
    { key: 'CLOSED', label: 'Closed', icon: 'fa-check', count: 0 },
    { key: 'WAITING_TAKING_CARE', label: 'Waiting taking care', icon: 'fa-clock-o', count: 0 },
    { key: 'CANCELLED', label: 'Cancelled', icon: 'fa-times', count: 0 },
    { key: 'all', label: 'All', icon: 'fa-list' }
  ];
  
  selectedStatus: string = 'all';
  

  priorityControl = new FormControl('');
  categoryControl = new FormControl('');
  fromControl = new FormControl('');
  toControl = new FormControl('');
  
  priorityOptions: SelectOption[] = [];
  categoryOptions: SelectOption[] = [];
  
  pageSize: number = 25;
  currentPage: number = 0;
  totalPages: number = 0;
  totalElements: number = 0;

  Math = Math;
  private paginationContainerSelector = '.primary-pagination';
  private averagePageItemWidth = 42;
  private navigationButtonsWidth = 100;

  constructor(
    private complaintsService: ComplaintsService,
    private optionService: OptionService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private el: ElementRef
  ) {}

  ngOnInit(): void {
    this.loadPriorityOptions();
    this.loadCategoryOptions();
    this.loadComplaints();
    this.loadStatusCounters();
    this.checkCreatePermission();
    this.setupFilterListeners();
  }

  loadPriorityOptions(): void {
    this.optionService.complaintPriorities().subscribe({
      next: (response: any) => {
        const priorities: SelectOption[] = [{ key: '', value: ' ' }];
        
        if (response?.data?.options) {
          const serverOptions = response.data.options.map((option: any) => ({
            key: option.key,
            value: option.value
          }));
          priorities.push(...serverOptions);
        }
        
        this.priorityOptions = priorities;
      },
      error: (error) => {
        console.error('Error loading priority options:', error);
        this.priorityOptions = [{ key: '', value: ' ' }];
      }
    });
  }

  loadCategoryOptions(): void {
    const params = new HttpParams();
    this.complaintsService.optionsCategories({ params }).subscribe({
      next: (response: any) => {
        const categories: SelectOption[] = [{ key: '', value: ' ' }];
        
        if (response?.data?.complaintCategories) {
          const serverOptions = response.data.complaintCategories.map((option: any) => ({
            key: option.complaintCategoryId,
            value: option.name
          }));
          categories.push(...serverOptions);
        }
        
        this.categoryOptions = categories;
      },
      error: (error) => {
        console.error('Error loading category options:', error);
        this.categoryOptions = [{ key: '', value: ' ' }];
      }
    });
  }

  setupFilterListeners(): void {
    this.priorityControl.valueChanges.subscribe(() => {
      this.currentPage = 0;
      this.loadComplaints();
    });

    this.categoryControl.valueChanges.subscribe(() => {
      this.currentPage = 0;
      this.loadComplaints();
    });

    this.fromControl.valueChanges.subscribe(() => {
      this.currentPage = 0;
      this.loadComplaints();
    });

    this.toControl.valueChanges.subscribe(() => {
      this.currentPage = 0;
      this.loadComplaints();
    });
  }

  checkCreatePermission(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      const allowedProfiles = ['CUS1', 'CUS2', 'CUS3'];
      this.canCreateComplaint = profileCode ? allowedProfiles.includes(profileCode) : false;
    });
  }

  loadStatusCounters(): void {
    this.complaintsService.countersStatus().subscribe({
      next: (response: any) => {
        if (response?.data?.statusCounters) {
          const counters = response.data.statusCounters;
          
          this.statusFilters.forEach(filter => {
            if (filter.key !== 'all') {
              const counter = counters.find((c: any) => c.status === filter.key);
              filter.count = counter ? counter.counter : 0;
            }
          });
        }
      },
      error: (error) => {
        console.error('Error loading status counters:', error);
      }
    });
  }

  loadComplaints(): void {
    let params = new HttpParams()
      .set('size', this.pageSize.toString())
      .set('page', this.currentPage.toString());

    if (this.selectedStatus && this.selectedStatus !== 'all') {
      params = params.set('status', this.selectedStatus);
    }

    if (this.searchTerm && this.searchTerm.trim() !== '') {
      params = params.set('text', this.searchTerm.trim());
    }

    const priority = this.priorityControl.value;
    if (priority && priority !== '') {
      params = params.set('priority', priority);
    }
    
    const category = this.categoryControl.value;
    if (category && category !== '') {
      params = params.set('complaintCategoryId', category);
    }
    
    const from = this.fromControl.value;
    if (from && from !== '') {
      params = params.set('complaintTimeStart', from);
    }
    
    const to = this.toControl.value;
    if (to && to !== '') {
      params = params.set('complaintTimeEnd', to);
    }

    this.complaintsService.list({ params }).subscribe({
      next: (response: any) => {
        this.complaints = response.data?.complaints || [];
        this.totalElements = response.pagination?.totalElements || 0;
        this.totalPages = response.pagination?.totalPages || 0;
        
        this.loadStatusCounters();
      },
      error: (error) => {
        console.error('Error loading complaints:', error);
        this.complaints = [];
      }
    });
  }

  selectStatus(status: string): void {
    this.selectedStatus = status;
    this.currentPage = 0;
    this.loadComplaints();
  }

  onSearchChange(): void {
    this.currentPage = 0;
    this.loadComplaints();
  }

  clearDetailFilters(): void {
    this.priorityControl.setValue('', { emitEvent: false });
    this.categoryControl.setValue('', { emitEvent: false });
    this.fromControl.setValue('', { emitEvent: false });
    this.toControl.setValue('', { emitEvent: false });
    this.currentPage = 0;
    this.loadComplaints();
  }

  getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      'OPEN': 'fa-gavel',
      'WAITING_FOR_CUSTOMER': 'fa-hourglass-start',
      'WAITING_FOR_OPERATOR': 'fa-hourglass-end',
      'CLOSED': 'fa-check',
      'WAITING_TAKING_CARE': 'fa-clock-o',
      'CANCELLED': 'fa-times'
    };
    return iconMap[status] || 'fa-gavel';
  }

  getStatusClass(reminderStatus: string): string {
    const statusMap: { [key: string]: string } = {
      'GREEN': 'status-green',
      'YELLOW': 'status-yellow',
      'RED': 'status-red'
    };
    return statusMap[reminderStatus] || 'status-gray';
  }

  getPriorityColorClass(priority: string): string {
    const priorityMap: { [key: string]: string } = {
      'LOW': 'priority-green',
      'MEDIUM': 'priority-yellow',
      'HIGH': 'priority-red'
    };
    return priorityMap[priority] || '';
  }

  viewComplaint(complaintId: number): void {
    this.routeService.complaintsUpdate(complaintId);
  }

  createNewComplaint(): void {
    this.routeService.complaintsCreate();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadComplaints();
  }

  changePage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadComplaints();
    }
  }

  private getMaxVisiblePageItems(): number {
    try {
      const paginationContainer = this.el.nativeElement.querySelector(this.paginationContainerSelector);
      if (!paginationContainer) {
        return 7;
      }
      
      const containerWidth = paginationContainer.clientWidth - this.navigationButtonsWidth;
      const maxItems = Math.floor(containerWidth / this.averagePageItemWidth);
      
      return Math.max(5, maxItems);
    } catch (error) {
      console.error('Error calculating visible elements of paging:', error);
      return 7;
    }
  }

  shouldUseCompactPagination(): boolean {
    const maxVisibleItems = this.getMaxVisiblePageItems();
    return this.totalPages > maxVisibleItems;
  }

  getAllPages(): number[] {
    const result: number[] = [];
    for (let i = 0; i < this.totalPages; i++) {
      result.push(i);
    }
    return result;
  }

  getVisiblePages(): number[] {
    if (this.totalPages === 0) {
      return [];
    }
    
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const maxVisibleItems = this.getMaxVisiblePageItems();
    const availableForWindow = Math.max(1, maxVisibleItems - 3);
    const pagesWindow = Math.floor(availableForWindow / 2);
    const result: number[] = [];
    
    let startPage = Math.max(1, currentPage - pagesWindow);
    let endPage = Math.min(totalPages - 2, currentPage + pagesWindow);
    
    if (startPage <= 1) {
      endPage = Math.min(totalPages - 2, 1 + (pagesWindow * 2));
    }
    if (endPage >= totalPages - 2) {
      startPage = Math.max(1, totalPages - 2 - (pagesWindow * 2));
    }
    
    for (let i = startPage; i <= endPage; i++) {
      if (i > 0 && i < totalPages - 1) {
        result.push(i);
      }
    }
    
    return result;
  }

  hasTakingCareUser(complaint: any): boolean {
    return !!complaint?.takingCareUser;
  }

  getAssignedToFullName(complaint: any): string {
    if (!complaint?.takingCareUser) {
      return '';
    }
    
    const firstName = complaint.takingCareUser.firstName || '';
    const lastName = complaint.takingCareUser.lastName || '';
    return `${firstName} ${lastName}`.trim();
  }
}
